import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
    selector: 'core-ui-textarea-form',
    templateUrl: './textarea-form.component.html',
    styleUrls: ['./textarea-form.component.scss'],
    standalone: false,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextareaFormComponent),
            multi: true,
        },
    ],
})
export class TextareaFormComponent implements ControlValueAccessor {
    @Input() label?: string;
    @Input() placeholder = '';
    @Input() readOnly = false;
    @Input() block = true;
    @Input() minRows = 4;
    @Input() maxRows?: number;
    @Input() resize: 'none' | 'vertical' = 'vertical';
    @Input() maxLength?: number;

    @Output() focused = new EventEmitter<void>();
    @Output() blurred = new EventEmitter<void>();
    @Output() keydown = new EventEmitter<KeyboardEvent>();
    @Output() keyPressed = new EventEmitter<KeyboardEvent>();
    @Output() inputChanged = new EventEmitter<HTMLTextAreaElement>();

    @Input()
    set modelValue(v: string | null) {
        this.value = v ?? '';
    }

    @Output() valueChange = new EventEmitter<string>();

    value = '';
    isFocused = false;
    disabled = false;

    private onChange: (v: string) => void = () => { };
    private onTouched: () => void = () => { };

    writeValue(v: string | null): void {
        this.value = v ?? '';
    }

    registerOnChange(fn: (v: string) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    @Input()
    set isDisabled(value: boolean) {
        this.disabled = value;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    get isReadOnlyFinal(): boolean {
        return this.readOnly || this.disabled;
    }

    get isFilled(): boolean {
        return (this.value ?? '').trim().length > 0;
    }

    get textareaStyle(): Record<string, string> {
        const style: Record<string, string> = {
            resize: this.resize,
        };

        if (this.maxRows && this.maxRows >= this.minRows) {
            style['max-height'] = `${this.maxRows * 24 + 39}px`;
        }

        return style;
    }

    handleInput(event: Event): void {
        if (this.disabled || this.readOnly) return;

        const textarea = event.target as HTMLTextAreaElement;
        this.value = textarea.value;

        this.onChange(this.value);
        this.valueChange.emit(this.value);
        this.inputChanged.emit(textarea);
    }

    handleBlur(): void {
        this.isFocused = false;
        this.onTouched();
        this.blurred.emit();
    }

    handleFocus(): void {
        if (this.disabled || this.readOnly) return;

        this.isFocused = true;
        this.focused.emit();
    }

    handleKeydown(event: KeyboardEvent): void {
        if (this.disabled || this.readOnly) return;

        this.keydown.emit(event);
        this.keyPressed.emit(event);
    }
}
